
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import {
  Menu,
  X,
  Instagram,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Smartphone,
  Building2,
  ShieldCheck,
  Star,
  Truck,
  Briefcase,
  MessageCircle
} from 'lucide-react';
import { NAV_ITEMS, BRANDS, BUSINESS_UNITS } from './constants';

/** 
 * REEMPLAZAR AQUÍ: URL del Logo de la empresa 
 */
const COMPANY_LOGO = "/LOG-WHITE-ARROW.PNG";

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentHeroIdx, setCurrentHeroIdx] = useState(0);

  /** 
   * REEMPLAZAR AQUÍ: Imágenes que rotan en el fondo de la sección principal (Hero)
   */
  const heroImages = [
    "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=2000",
    "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&q=80&w=2000",
    "https://images.unsplash.com/photo-1578916171728-46686eac8d58?auto=format&fit=crop&q=80&w=2000"
  ];

  const { scrollYProgress } = useScroll();
  const yHero = useTransform(scrollYProgress, [0, 0.5], [0, -50]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    const interval = setInterval(() => {
      setCurrentHeroIdx((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  const containerClasses = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-blue-600 selection:text-white">

      {/* WhatsApp Floating */}
      <a
        href="https://wa.me/5493413516170"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[110] bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform flex items-center justify-center"
      >
        <svg
          viewBox="0 0 24 24"
          width="28"
          height="28"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      </a>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${scrolled || isMenuOpen ? 'bg-slate-950/80 backdrop-blur-md shadow-md py-3' : 'bg-transparent py-6'}`}>
        <div className={containerClasses}>
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img
                src={COMPANY_LOGO}
                alt="Arrow Logo"
                className="h-10 md:h-12 w-auto transition-all"
              />
            </div>

            <div className="hidden md:flex items-center gap-8">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className={`text-xs font-bold uppercase tracking-widest transition-colors hover:text-blue-500 ${scrolled ? 'text-slate-200' : 'text-white'}`}
                >
                  {item.label}
                </a>
              ))}
              <a href="#contacto" className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${scrolled ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-blue-600'}`}>
                Presupuesto
              </a>
            </div>

            <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={28} className="text-white" /> : <Menu size={28} className="text-white" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            className="fixed inset-0 z-[95] bg-slate-950 flex flex-col pt-24 px-8"
          >
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setIsMenuOpen(false)}
                className="text-4xl font-display font-black text-white mb-6 border-b border-slate-800 pb-2"
              >
                {item.label}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section id="inicio" className="relative h-[90vh] min-h-[600px] flex items-center overflow-hidden bg-slate-950">
        <div className="absolute inset-0 z-0">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentHeroIdx}
              src={heroImages[currentHeroIdx]}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 0.4, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
        </div>

        <motion.div style={{ y: yHero }} className={`${containerClasses} relative z-10 w-full`}>
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-[10px] font-black uppercase tracking-[0.2em] mb-6"
            >
              <Star size={12} className="fill-current" /> Trayectoria y Confianza
            </motion.div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-black text-white leading-[0.9] tracking-tighter mb-8">
              Distribuidora <br />
              <span className="text-blue-500">mayorista regional.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl font-medium leading-relaxed mb-10">
              Llevamos las marcas líderes de Argentina a su góndola con logística propia y eficiencia operativa garantizada.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contacto" className="px-8 py-4 bg-blue-600 text-white font-bold uppercase tracking-widest text-xs rounded-xl shadow-xl shadow-blue-900/20 hover:bg-blue-700 transition-all flex items-center gap-2">
                Contactar Ahora <ArrowRight size={16} />
              </a>
              <a href="#capacidades" className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-bold uppercase tracking-widest text-xs rounded-xl hover:bg-white/20 transition-all border border-white/10">
                Nuestras Unidades
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Quick Stats Bar */}
      <div className="relative z-20 -mt-16">
        <div className={containerClasses}>
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { label: "Años en el mercado", val: "22+", Icon: Star },
              { label: "Cobertura", val: "Regional", Icon: MapPin },
              { label: "Logística Propia", val: "100%", Icon: Truck },
              { label: "Marcas", val: "50+", Icon: Briefcase },
            ].map((stat, i) => (
              <div key={i} className="text-center border-r last:border-0 border-slate-100 px-4 md:px-0">
                <stat.Icon className="text-blue-600 mb-4 mx-auto" size={48} />
                <div className="text-3xl lg:text-4xl font-black text-slate-900 mb-2">{stat.val}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Business Units */}
      <section id="capacidades" className="py-32 bg-white">
        <div className={containerClasses}>
          <div className="mb-20 text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-6">Nuestras Unidades de Negocio</h2>
            <p className="text-slate-500 text-lg">Estructura profesionalizada para dar soporte a las necesidades de abastecimiento de toda la región litoral.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {BUSINESS_UNITS.map((unit) => (
              <div key={unit.id} className="group flex flex-col bg-slate-50 rounded-3xl overflow-hidden border border-slate-100 hover:shadow-2xl transition-all duration-500">
                <div className="h-48 overflow-hidden relative">
                  <img src={unit.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={unit.title} />
                  <div className="absolute inset-0 bg-blue-600/10" />
                </div>
                <div className="p-8 flex flex-col flex-grow">
                  <div className="mb-4 text-blue-600">
                    {unit.icon === 'Building2' && <Building2 size={32} />}
                    {unit.icon === 'ShieldCheck' && <ShieldCheck size={32} />}
                    {unit.icon === 'Smartphone' && <Smartphone size={32} />}
                  </div>
                  <h3 className="text-xl font-black mb-1">{unit.title}</h3>
                  <div className="text-blue-600 text-[10px] font-black uppercase tracking-wider mb-4">{unit.subtitle}</div>
                  <p className="text-slate-600 text-sm mb-6 leading-relaxed flex-grow">{unit.description}</p>
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-200">
                    {unit.tags.map(tag => (
                      <span key={tag} className="text-[9px] font-bold uppercase tracking-tighter bg-white px-2 py-1 rounded-md text-slate-400 border border-slate-200">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prime Alliance Section */}
      {/* Prime Alliance Section */}
      <section className="min-h-screen flex items-center py-24 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-40">
          <img src="/seccionprime.jpg" alt="Prime Background" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />

        <div className={`${containerClasses} relative z-10 w-full`}>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="mb-12">
                <img src="/logo-blanco-prime.webp" alt="Prime Logo" className="h-32 md:h-40 w-auto mb-10 opacity-90" />
                <h2 className="text-5xl md:text-7xl font-display font-black tracking-tighter mb-8 leading-[0.9]">
                  REPRESENTANTES <br /> <span className="text-blue-500">OFICIALES</span>
                </h2>
                <p className="text-slate-300 text-lg md:text-xl leading-relaxed mb-10 max-w-xl">
                  Como distribuidores exclusivos de la línea Prime, garantizamos la presencia de la marca líder en salud sexual y bienestar en su comercio. Productos de máxima confianza con logística de alta rotación.
                </p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-600 p-3 rounded-xl text-white shadow-lg shadow-blue-900/50">
                      <ShieldCheck size={24} />
                    </div>
                    <div>
                      <span className="block font-black text-lg">Trazabilidad</span>
                      <span className="text-sm text-slate-400">Total Garantizada</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-600 p-3 rounded-xl text-white shadow-lg shadow-blue-900/50">
                      <Star size={24} />
                    </div>
                    <div>
                      <span className="block font-black text-lg">Calidad</span>
                      <span className="text-sm text-slate-400">100% Certificada</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] md:aspect-square rounded-[3rem] overflow-hidden shadow-2xl border border-white/10 rotate-3 hover:rotate-0 transition-all duration-700">
                <img src="/seccionprime2.jpg" alt="Productos Prime" className="w-full h-full object-cover scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section id="nosotros" className="py-32 bg-white relative overflow-hidden">
        <div className={containerClasses}>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img src="/camionetaarrow.jpeg" alt="Camioneta Arrow" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-8 -right-8 bg-blue-600 p-8 rounded-3xl text-white shadow-xl hidden md:block"
              >
                <div className="text-4xl font-black mb-1">2001</div>
                <div className="text-xs font-bold uppercase tracking-widest opacity-80">Año de Inicio</div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-8 text-slate-900">
                ¿Quiénes <span className="text-blue-600">Somos?</span>
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>
                  Arrow Distribuciones nace en el año 2001 en respuesta a necesidades insatisfechas en el mercado en lo que hace al servicio de venta y distribución orientado hacia ambas partes de la cadena.
                </p>
                <p>
                  Somos una empresa local dedicada a la comercialización y distribución de diferentes productos con distintas modalidades.
                </p>
              </div>
              <div className="mt-10">
                <a href="#contacto" className="inline-flex items-center gap-2 text-blue-600 font-bold uppercase tracking-widest text-xs hover:text-blue-700 transition-colors border-b-2 border-blue-600/20 pb-1 hover:border-blue-600">
                  Conocer más <ArrowRight size={14} />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brands Slider */}
      <section className="py-20 bg-slate-50 overflow-hidden border-y border-slate-100">
        <div className="mb-10 text-center">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Marcas que confían en nosotros</span>
        </div>
        <div className="flex items-center gap-16 animate-marquee whitespace-nowrap">
          {[...BRANDS, ...BRANDS].map((brand, idx) => (
            <div key={idx} className="flex-shrink-0 grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
              <img src={brand.logo} alt={brand.name} className="h-10 w-auto object-contain" />
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-32 bg-slate-900 text-white overflow-hidden relative">
        <div className={containerClasses}>
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-8">Impulsamos su <br /> <span className="text-blue-500">punto de venta.</span></h2>
              <div className="space-y-8">
                {[
                  { icon: Phone, label: "Ventas", val: "+54 341 3516170" },
                  { icon: Mail, label: "Consultas", val: "info@arrowdistribuciones.com.ar" },
                  { icon: MapPin, label: "Logística", val: "Rosario, Santa Fe" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="bg-blue-600/20 p-3 rounded-xl text-blue-500">
                      <item.icon size={20} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">{item.label}</div>
                      <div className="text-xl font-medium">{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-10 rounded-3xl text-slate-900 shadow-2xl">
              <h3 className="text-2xl font-black mb-6">Solicitar Presupuesto</h3>
              <form className="space-y-4" onSubmit={e => e.preventDefault()}>
                <input type="text" placeholder="Nombre de contacto" className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl outline-none focus:border-blue-600 transition-colors" />
                <input type="email" placeholder="Correo electrónico" className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl outline-none focus:border-blue-600 transition-colors" />
                <textarea placeholder="Detalle de productos o consulta" rows={4} className="w-full bg-slate-50 border border-slate-200 p-4 rounded-xl outline-none focus:border-blue-600 transition-colors resize-none" />
                <button className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold uppercase tracking-widest hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20">
                  Enviar Mensaje
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 text-slate-600 border-t border-white/5">
        <div className={containerClasses}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
            <div className="flex items-center gap-4">
              <img src={COMPANY_LOGO} alt="Arrow Logo" className="h-8 w-auto opacity-80" />
              <div className="text-xs">© {new Date().getFullYear()} Arrow Distribuciones. <br className="md:hidden" /> Todos los derechos reservados.</div>
            </div>
            <div className="flex gap-6">
              <a href="https://instagram.com/arrow.distribuciones" target="_blank" rel="noopener" className="hover:text-white transition-colors"><Instagram size={20} /></a>
              <a href="#" className="text-[10px] font-bold uppercase tracking-widest hover:text-white transition-colors">Privacidad</a>
              <a href="#" className="text-[10px] font-bold uppercase tracking-widest hover:text-white transition-colors">Términos</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
